import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { UserRole } from './types';
import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import Login from './pages/Login';
import MobileTableLabels from './components/MobileTableLabels';
import BottomNav from './components/BottomNav';
import WorkshopGate from './workshop/WorkshopGate';
import StarfieldBackground from './components/StarfieldBackground';
import MobileShell from './mobile/chrome/MobileShell';
import './App.css';
import './mobile.css'; // global mobile hardening — imported last so it wins the cascade
import './mobile/mobile-ui.css'; // the phone app layer (src/mobile) — after mobile.css on purpose
import './mobile/views/views.css'; // phone screens — global, so a page reached directly is never unstyled
import { BrandProvider } from './branding/BrandContext';
import './branding/brands.css'; // white-label workshop layers (html[data-brand]) — last, so they win ties

/**
 * Routes are code-split.
 *
 * Every page used to sit in one 2.35 MB bundle, so opening the LOGIN screen
 * made a phone download, parse and compile the whole admin analytics suite and
 * the 6,000-line order page before it could show a password box. Gzip hides
 * that on the network tab but not on the CPU, which is what makes a phone feel
 * slow. Each page is now its own chunk, fetched when the route is opened.
 */
/**
 * PREFETCH THE ROUTES PEOPLE ACTUALLY MOVE BETWEEN.
 *
 * Every lazy route was fetched at CLICK time — the build emitted no prefetch or preload at all,
 * so opening an order meant: request the chunk, wait a round trip, download 84 KB, and only
 * then mount and start asking for data. That is the "it takes seconds to load an empty order"
 * everyone feels, and it returns after every deploy, because content hashing gives every chunk
 * a new name and empties the cache.
 *
 * `webpackPrefetch` emits <link rel="prefetch">, which the browser fetches at IDLE priority
 * once the current page is usable. The click then finds the chunk in cache and renders at once.
 *
 * Only these four: they are the ones people bounce between all day. Prefetching all forty would
 * hand every phone the whole app and defeat the splitting.
 */
const Dashboard = lazy(() => import(/* webpackPrefetch: true */ './pages/Dashboard'));
// Lazy like every other page. Statically imported, these two rode in the MAIN bundle — the one
// every user downloads on every visit — even though most roles never open either. PartsCatalog
// alone is ~1,900 lines. Both routes already sit inside the Suspense boundary below.
const QuoteLeads = lazy(() => import('./pages/QuoteLeads'));
const PartsCatalog = lazy(() => import('./pages/PartsCatalog'));
const Delivery = lazy(() => import('./pages/Delivery'));
const ActivityLog = lazy(() => import('./pages/ActivityLog'));
const DeliveryRunSheet = lazy(() => import('./pages/DeliveryRunSheet'));
const PartsFitment = lazy(() => import('./pages/PartsFitment'));
const PartsCrossMake = lazy(() => import('./pages/PartsCrossMake'));
const Orders = lazy(() => import(/* webpackPrefetch: true */ './pages/Orders'));
const OrderDetail = lazy(() => import(/* webpackPrefetch: true */ './pages/OrderDetail'));
const CreateOrder = lazy(() => import(/* webpackPrefetch: true */ './pages/CreateOrder'));
const Bids = lazy(() => import('./pages/Bids'));
const SupplierProfile = lazy(() => import('./pages/SupplierProfile'));
const WorkshopAssistant = lazy(() => import('./pages/WorkshopAssistant'));
const BonusManagement = lazy(() => import('./pages/BonusManagement'));
const MyCash = lazy(() => import('./pages/MyCash'));
const SupplierReconciliation = lazy(() => import('./pages/SupplierReconciliation'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminMasterData = lazy(() => import('./pages/AdminMasterData'));
const AdminBot = lazy(() => import('./pages/AdminBot'));
const WorkshopPosSettings = lazy(() => import('./pages/WorkshopPosSettings'));
const WorkshopLocations = lazy(() => import('./pages/WorkshopLocations'));
const SupplierLabourLinks = lazy(() => import('./pages/SupplierLabourLinks'));
const PublicLabourLink = lazy(() => import('./pages/PublicLabourLink'));
const DeliveryNoteUpload = lazy(() => import('./pages/DeliveryNoteUpload'));
const AdminLabourLinks = lazy(() => import('./pages/AdminLabourLinks'));
const AdminAnalytics = lazy(() => import('./pages/AdminAnalytics'));
const AnalyticsCenter = lazy(() => import('./pages/AnalyticsCenter'));
const Books = lazy(() => import('./pages/Books'));
const AdminStaffKpis = lazy(() => import('./pages/AdminStaffKpis'));
const PurchaseInvoices = lazy(() => import('./pages/PurchaseInvoices'));
const Reconciliation = lazy(() => import('./pages/Reconciliation'));
const ExpenseAudit = lazy(() => import('./pages/ExpenseAudit'));
const Feedback = lazy(() => import('./pages/Feedback'));
const JobCardApproval = lazy(() => import('./pages/JobCardApproval'));
const OrderPayment = lazy(() => import('./pages/OrderPayment'));
const SupplierQuote = lazy(() => import('./pages/SupplierQuote'));
const Feedbacks = lazy(() => import('./pages/Feedbacks'));
const CustomerProfilePage = lazy(() => import('./pages/CustomerProfile'));
const AdminCustomers = lazy(() => import('./pages/AdminCustomers'));
const JobCards = lazy(() => import('./pages/JobCards'));
const JobCardDetail = lazy(() => import('./pages/JobCardDetail'));
const Estimate = lazy(() => import('./pages/Estimate'));
const EstimateDetail = lazy(() => import('./pages/EstimateDetail'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Technicians = lazy(() => import('./pages/Technicians'));
const LiveBays = lazy(() => import('./pages/LiveBays'));
const PartsEstimator = lazy(() => import('./pages/PartsEstimator'));
const AdminPriceList = lazy(() => import('./pages/AdminPriceList'));
const PriceComparison = lazy(() => import('./pages/PriceComparison'));
const Assistant = lazy(() => import('./pages/Assistant'));
// Workshop v2 («الواجهة الجديدة للورشة», workshops.ui_v2): WorkshopGate shows these to a v2 workshop login and the
// current page to everyone else — so nobody else ever downloads these chunks.
const WorkshopHome = lazy(() => import('./workshop/home/WorkshopHome'));
const WorkshopOrders = lazy(() => import('./workshop/orders/WorkshopOrders'));
const WorkshopOrderPage = lazy(() => import('./workshop/order/WorkshopOrderPage'));
// Wave 2 (placeholders until each builder lands its page).
const WorkshopInsights = lazy(() => import('./workshop/insights/WorkshopInsights'));
const WorkshopMoney = lazy(() => import('./workshop/money/WorkshopMoney'));
const WorkshopCustomers = lazy(() => import('./workshop/customers/WorkshopCustomers'));
const WorkshopCustomerPage = lazy(() => import('./workshop/customers/WorkshopCustomerPage'));
const WorkshopTeam = lazy(() => import('./workshop/team/WorkshopTeam'));
const WorkshopCreateOrder = lazy(() => import('./workshop/create/WorkshopCreateOrder'));
/** The `legacy` side of a v2-only path (/insights, /money, /customers, /team): everyone else goes home, as before. */
const ToHome: React.FC = () => <Navigate to="/" replace />;
/** Old workshop pages folded into the v2 ones (spec §2.4) — only a v2 workshop is redirected; everyone else keeps the page. */
const ToMoneyCustody: React.FC = () => <Navigate to="/money?tab=custody" replace />;
const ToTeamRatings: React.FC = () => <Navigate to="/team?tab=ratings" replace />;
const ToTeamBonus: React.FC = () => <Navigate to="/team?tab=bonus" replace />;

const PrivateRoute: React.FC<{ children: React.ReactNode; superAdminOnly?: boolean }> = ({ children, superAdminOnly }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Super-admin-only pages (e.g. Users, Master Data): redirect everyone else home.
  if (superAdminOnly && user?.role !== UserRole.SUPER_ADMIN) {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <StarfieldBackground />
          <Router>
            {/* A white-label workshop's look (src/branding) — needs the user and the route. */}
            <BrandProvider>
            {/* Phone app frame: top bar, «المزيد» sheet, notifications, toasts (≤768px only). */}
            <MobileShell>
            <MobileTableLabels />
            {/* App-style tab bar on phones. Renders nothing on desktop, on
                public pages, or when signed out — see the component. */}
            <BottomNav />
            {/* Each route is its own chunk now; this covers the moment it loads. */}
            <Suspense fallback={<div className="loading">Loading…</div>}>
            <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/feedback/:orderId" element={<Feedback />} />
          <Route path="/quote/:orderId" element={<SupplierQuote />} />
          <Route path="/jc/:id/approve" element={<JobCardApproval />} />
          <Route path="/pay/:id" element={<OrderPayment />} />
          <Route path="/labour/:token" element={<PublicLabourLink />} />
          {/* The QR on a printed «سند تسليم»: the signed copy is photographed and sent from here. */}
          <Route path="/d/:token" element={<DeliveryNoteUpload />} />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <WorkshopGate v2={WorkshopHome} legacy={Dashboard} />
              </PrivateRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <PrivateRoute>
                <WorkshopGate v2={WorkshopOrders} legacy={Orders} />
              </PrivateRoute>
            }
          />
          <Route
            path="/orders/create"
            element={
              <PrivateRoute>
                <WorkshopGate v2={WorkshopCreateOrder} legacy={CreateOrder} />
              </PrivateRoute>
            }
          />
          <Route
            path="/orders/:id"
            element={
              <PrivateRoute>
                <WorkshopGate v2={WorkshopOrderPage} legacy={OrderDetail} />
              </PrivateRoute>
            }
          />
          <Route
            path="/customers/:id"
            element={
              <PrivateRoute>
                <WorkshopGate v2={WorkshopCustomerPage} legacy={CustomerProfilePage} />
              </PrivateRoute>
            }
          />
          {/* Workshop v2 only — anyone else is sent home (these paths did not exist before). */}
          <Route
            path="/customers"
            element={
              <PrivateRoute>
                <WorkshopGate v2={WorkshopCustomers} legacy={ToHome} />
              </PrivateRoute>
            }
          />
          <Route
            path="/insights"
            element={
              <PrivateRoute>
                <WorkshopGate v2={WorkshopInsights} legacy={ToHome} />
              </PrivateRoute>
            }
          />
          <Route
            path="/insights/:section"
            element={
              <PrivateRoute>
                <WorkshopGate v2={WorkshopInsights} legacy={ToHome} />
              </PrivateRoute>
            }
          />
          <Route
            path="/money"
            element={
              <PrivateRoute>
                <WorkshopGate v2={WorkshopMoney} legacy={ToHome} />
              </PrivateRoute>
            }
          />
          <Route
            path="/team"
            element={
              <PrivateRoute>
                <WorkshopGate v2={WorkshopTeam} legacy={ToHome} />
              </PrivateRoute>
            }
          />
          <Route
            path="/bids"
            element={
              <PrivateRoute>
                <Bids />
              </PrivateRoute>
            }
          />
          <Route
            path="/assistant"
            element={
              <PrivateRoute>
                <WorkshopAssistant />
              </PrivateRoute>
            }
          />
          <Route
            path="/supplier/profile"
            element={
              <PrivateRoute>
                <SupplierProfile />
              </PrivateRoute>
            }
          />
          <Route
            path="/my-cash"
            element={
              <PrivateRoute>
                <WorkshopGate v2={ToMoneyCustody} legacy={MyCash} />
              </PrivateRoute>
            }
          />
          <Route
            path="/bonus"
            element={
              <PrivateRoute>
                <WorkshopGate v2={ToTeamBonus} legacy={BonusManagement} />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <PrivateRoute superAdminOnly>
                <AdminUsers />
              </PrivateRoute>
            }
          />
          {/* «الإعدادات»: one route with an optional section, so moving between sections never remounts the page. */}
          <Route
            path="/admin/master-data/:section?"
            element={
              <PrivateRoute superAdminOnly>
                <AdminMasterData />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/bot"
            element={
              <PrivateRoute superAdminOnly>
                <AdminBot />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/activity"
            element={
              <PrivateRoute superAdminOnly>
                <ActivityLog />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/delivery/runs/:id/sheet"
            element={
              <PrivateRoute>
                <DeliveryRunSheet />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/delivery"
            element={
              <PrivateRoute>
                <Delivery />
              </PrivateRoute>
            }
          />
          <Route
            path="/parts-catalog"
            element={
              <PrivateRoute>
                <PartsCatalog />
              </PrivateRoute>
            }
          />
          <Route
            path="/parts-catalog/fitment"
            element={
              <PrivateRoute>
                <PartsFitment />
              </PrivateRoute>
            }
          />
          <Route
            path="/parts-catalog/cross-make"
            element={
              <PrivateRoute>
                <PartsCrossMake />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/pos"
            element={
              <PrivateRoute superAdminOnly>
                <WorkshopPosSettings />
              </PrivateRoute>
            }
          />
          <Route
            path="/supplier/labour-links"
            element={
              <PrivateRoute>
                <SupplierLabourLinks />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/labour-links"
            element={
              <PrivateRoute superAdminOnly>
                <AdminLabourLinks />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/locations"
            element={
              <PrivateRoute superAdminOnly>
                <WorkshopLocations />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <PrivateRoute>
                <AnalyticsCenter />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/analytics/:section"
            element={
              <PrivateRoute>
                <AnalyticsCenter />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/books"
            element={
              <PrivateRoute>
                <Books />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/books/:section"
            element={
              <PrivateRoute>
                <Books />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/books/:section/:sub"
            element={
              <PrivateRoute>
                <Books />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/analytics/classic"
            element={
              <PrivateRoute>
                <AdminAnalytics />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/staff-kpis"
            element={
              <PrivateRoute>
                <AdminStaffKpis />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/customers"
            element={
              <PrivateRoute>
                <AdminCustomers />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/inventory"
            element={
              <PrivateRoute>
                <Inventory />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/technicians"
            element={
              <PrivateRoute>
                <Technicians />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/live-bays"
            element={
              <PrivateRoute>
                <LiveBays />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/parts-estimator"
            element={
              <PrivateRoute>
                <PartsEstimator />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/price-list"
            element={
              <PrivateRoute>
                <AdminPriceList />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/price-comparison"
            element={
              <PrivateRoute>
                <PriceComparison />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/assistant"
            element={
              <PrivateRoute superAdminOnly>
                <Assistant />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/estimates"
            element={
              <PrivateRoute>
                <Estimate />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/estimates/:id"
            element={
              <PrivateRoute>
                <EstimateDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/purchase-invoices"
            element={
              <PrivateRoute>
                <PurchaseInvoices />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/quote-leads"
            element={
              <PrivateRoute>
                <QuoteLeads />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reconciliation"
            element={
              <PrivateRoute>
                <Reconciliation />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/supplier-reconciliation"
            element={
              <PrivateRoute>
                <SupplierReconciliation />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/expense-audit"
            element={
              <PrivateRoute superAdminOnly>
                <ExpenseAudit />
              </PrivateRoute>
            }
          />
          {/* The old «فاتورة مبيعات» tool posted to Daftra (removed 2026-11-01) — old links land in the books' sales. */}
          <Route path="/admin/sales-invoice" element={<Navigate to="/admin/books/sales" replace />} />
          <Route
            path="/feedbacks"
            element={
              <PrivateRoute>
                <WorkshopGate v2={ToTeamRatings} legacy={Feedbacks} />
              </PrivateRoute>
            }
          />
          <Route
            path="/job-cards"
            element={
              <PrivateRoute>
                <JobCards />
              </PrivateRoute>
            }
          />
          <Route
            path="/job-cards/:id"
            element={
              <PrivateRoute>
                <JobCardDetail />
              </PrivateRoute>
            }
          />
            </Routes>
            </Suspense>
            </MobileShell>
            </BrandProvider>
          </Router>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
